import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Space, Typography, notification } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import PageWrapper from '../../../ui/PageWrapper';
import { CounterpartyService } from '../../../services/CounterpartyService';
import { CounterpartyActivityClassifierDtoResponse } from '../../../interfaces/CounterpartyDtos';
import { DictionaryCatalogDtoResponse } from '../../../interfaces/DictionaryCatalogDtoResponse';


const ActivityClassifierPage = () => {
    const [data, setData] = useState<DictionaryCatalogDtoResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState<DictionaryCatalogDtoResponse | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchActivityClassifiers();
    }, []);

    const fetchActivityClassifiers = async () => {
        setLoading(true);
        try {
            const response = await CounterpartyService.getClassifiers();
            setData(response.data);
        } catch (error) {
            notification.error({ message: "Error fetching data" });
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingItem(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record: DictionaryCatalogDtoResponse) => {
        setEditingItem(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = async (id: number) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this item?',
            onOk: async () => {
                setLoading(true);
                try {
                    await CounterpartyService.deleteClassifier(id);
                    notification.success({ message: "Activity classifier deleted" });
                    fetchActivityClassifiers();
                } catch (error) {
                    notification.error({ message: "Error deleting activity classifier" });
                } finally {
                    setLoading(false);
                }
            },
        });
    };


    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (editingItem) {
                await CounterpartyService.updateClassifier(editingItem.id, values);
                notification.success({ message: "Activity classifier successfully updated" });
            } else {
                await CounterpartyService.createClassifier(values);
                notification.success({ message: "Activity classifier successfully created" });
            }

            setIsModalVisible(false);
            fetchActivityClassifiers();
        } catch (error) {
            console.error("Error saving activity classifier:", error);
            notification.error({ message: "Error saving activity classifier" });
        } finally {
            setLoading(false);
        }
    };


    const columns = [
        { title: 'Name (KZ)', dataIndex: 'name', key: 'name' },
        { title: 'Name (RU)', dataIndex: 'nameRu', key: 'nameRu' },
        { title: 'Name (EN)', dataIndex: 'nameEn', key: 'nameEn' },
        {
            title: 'Actions',
            key: 'actions',
            render: (record: DictionaryCatalogDtoResponse) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)} />
                </Space>
            ),
        },
    ];

    return (
        <PageWrapper>
            <Typography.Title level={1}>Activity Classifier</Typography.Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ marginBottom: 16 }}>
                Add Activity Classifier
            </Button>
            <Table columns={columns} dataSource={data} loading={loading} rowKey="id" />
            <Modal
                title={editingItem ? 'Edit Activity Classifier' : 'Add Activity Classifier'}
                visible={isModalVisible}
                onOk={handleSave}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Name (KZ)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="nameRu" label="Name (RU)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="nameEn" label="Name (EN)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </PageWrapper>
    );
};

export default ActivityClassifierPage;
